import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./UserReferralNetworkPage.css";

const LEVEL_TABS = ["all", "1", "2", "3", "4", "5"];
const COMMISSION_TABS = [
  { key: "all", label: "All" },
  { key: "staking", label: "Staking" },
  { key: "brokerage", label: "Brokerage" },
  { key: "referral_rewards", label: "Referral" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatAmount = (n) => {
  const num = Number(n ?? 0);
  if (!Number.isFinite(num)) return "0";
  return num.toLocaleString(undefined, { maximumFractionDigits: 8 });
};

const userName = (u) => {
  if (!u) return "—";
  const parts = [
    u.firstName ?? u.first_name,
    u.lastName ?? u.last_name,
  ].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const userEmail = (u) => u?.emailId ?? u?.email ?? u?.email_masked ?? "—";

const userInitials = (u) => {
  const name = userName(u);
  if (name === "—") return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const sourceLabel = (row) => {
  const src = row?.source;
  if (src === "staking_commission") {
    const t = row?.commission_type === "instant_bonus" ? "Instant bonus" : "Staking payout";
    return t;
  }
  if (src === "brokerage") return "Brokerage";
  if (src === "referral_reward") {
    return row?.reward_type === "signup_bonus" ? "Signup bonus" : "Referral bonus";
  }
  return src || "—";
};

const sourceBadgeClass = (row) => {
  const src = row?.source;
  if (src === "staking_commission") return "urn-badge urn-badge--staking";
  if (src === "brokerage") return "urn-badge urn-badge--brokerage";
  if (src === "referral_reward") return "urn-badge urn-badge--referral";
  return "urn-badge";
};

const commissionDetail = (row) => {
  if (row?.source === "brokerage") {
    return row.trade_id ? `Trade ${row.trade_id}` : "—";
  }
  if (row?.source === "staking_commission") {
    return row.plan_id ? `Plan ${String(row.plan_id).slice(-8)}` : "—";
  }
  if (row?.source === "referral_reward") {
    return row.reward_type?.replace(/_/g, " ") || "—";
  }
  return "—";
};

const EarningsMini = ({ earnings }) => {
  if (!earnings) return <span className="urn-muted">—</span>;
  return (
    <div className="urn-earnings-mini">
      <span title="Staking">
        <i className="fas fa-seedling" /> {formatAmount(earnings.staking_commission?.total)}
      </span>
      <span title="Brokerage">
        <i className="fas fa-chart-line" /> {formatAmount(earnings.brokerage?.total)}
      </span>
      <span title="Referral">
        <i className="fas fa-gift" /> {formatAmount(earnings.referral_rewards?.total)}
      </span>
      <strong title="Combined">{formatAmount(earnings.combined_total)}</strong>
    </div>
  );
};

const FLOW_MEMBERS_PREVIEW = 12;

const NetworkFlowDiagram = ({ rootUser, levels, summary }) => {
  const [expandedLevels, setExpandedLevels] = useState(() => new Set());

  const levelRows = useMemo(() => {
    const byLevel = summary?.by_level || {};
    return [1, 2, 3, 4, 5]
      .map((n) => {
        const key = String(n);
        const fromSummary = byLevel[key];
        const fromLevels = levels?.find((l) => l.level === n);
        const count = fromLevels?.count ?? fromSummary?.members ?? 0;
        const members = fromLevels?.members ?? [];
        if (count === 0 && members.length === 0) return null;
        return { level: n, count, members, earnings: fromLevels?.level_earnings ?? fromSummary?.earnings };
      })
      .filter(Boolean);
  }, [levels, summary]);

  const toggleLevelMembers = (level) => {
    setExpandedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  };

  return (
    <div className="urn-flow">
      <div className="urn-flow__root">
        <div className="urn-flow__node urn-flow__node--root">
          <span className="urn-flow__node-badge">Root</span>
          <strong>{userName(rootUser)}</strong>
          <span className="urn-flow__node-meta">{rootUser?.uuid || "—"}</span>
          <span className="urn-flow__node-meta">{userEmail(rootUser)}</span>
          <code className="urn-flow__node-code">{rootUser?.referral_code || "—"}</code>
        </div>
      </div>

      {levelRows.length === 0 ? (
        <p className="urn-flow__empty">No downline members in this network.</p>
      ) : (
        levelRows.map((row, idx) => (
          <React.Fragment key={row.level}>
            <div className="urn-flow__connector" aria-hidden>
              <div className="urn-flow__connector-line" />
              <div className="urn-flow__connector-arrow">
                <i className="fas fa-chevron-down" />
              </div>
            </div>
            <div className="urn-flow__level">
              <div className="urn-flow__level-head">
                <span className="urn-flow__level-pill">Level {row.level}</span>
                <span className="urn-flow__level-count">{row.count} member{row.count !== 1 ? "s" : ""}</span>
                <EarningsMini earnings={row.earnings} />
              </div>
              <div className="urn-flow__members">
                {(expandedLevels.has(row.level)
                  ? row.members
                  : row.members.slice(0, FLOW_MEMBERS_PREVIEW)
                ).map((m) => (
                  <div key={m._id} className="urn-flow__member" title={userEmail(m)}>
                    <span className="urn-flow__member-avatar">{userInitials(m)}</span>
                    <span className="urn-flow__member-name">{userName(m)}</span>
                    <span className="urn-flow__member-sub">{m.uuid || m.referral_code || "—"}</span>
                  </div>
                ))}
                {row.members.length > FLOW_MEMBERS_PREVIEW && !expandedLevels.has(row.level) && (
                  <button
                    type="button"
                    className="urn-flow__member urn-flow__member--more"
                    onClick={() => toggleLevelMembers(row.level)}
                    aria-expanded={false}
                    aria-label={`Show ${row.members.length - FLOW_MEMBERS_PREVIEW} more members at level ${row.level}`}
                  >
                    <i className="fas fa-plus-circle" aria-hidden />
                    +{row.members.length - FLOW_MEMBERS_PREVIEW} more
                  </button>
                )}
                {expandedLevels.has(row.level) && row.members.length > FLOW_MEMBERS_PREVIEW && (
                  <button
                    type="button"
                    className="urn-flow__member urn-flow__member--less"
                    onClick={() => toggleLevelMembers(row.level)}
                    aria-expanded={true}
                    aria-label={`Show fewer members at level ${row.level}`}
                  >
                    <i className="fas fa-minus-circle" aria-hidden />
                    Show less
                  </button>
                )}
              </div>
            </div>
            {idx === levelRows.length - 1 && (
              <div className="urn-flow__connector urn-flow__connector--end" aria-hidden>
                <div className="urn-flow__connector-line urn-flow__connector-line--dashed" />
              </div>
            )}
          </React.Fragment>
        ))
      )}
    </div>
  );
};

const MemberRow = ({ member, expanded, onToggle }) => {
  const earn = member.earnings_for_root_user;
  const logs = member.commission_logs || {};

  return (
    <div className={`urn-member-card ${expanded ? "urn-member-card--open" : ""}`}>
      <button type="button" className="urn-member-card__head" onClick={onToggle}>
        <div className="urn-member-card__user">
          <span className="urn-member-card__avatar" aria-hidden="true">{userInitials(member)}</span>
          <div>
            <strong>{userName(member)}</strong>
            <span>{userEmail(member)}</span>
            <span className="urn-member-card__meta">
              L{member.level} · {member.uuid || "—"} · Ref: {member.referral_code || "—"}
            </span>
          </div>
        </div>
        <div className="urn-member-card__summary">
          <EarningsMini earnings={earn} />
          <i className={`fas fa-chevron-${expanded ? "up" : "down"}`} />
        </div>
      </button>
      {expanded && (
        <div className="urn-member-card__body">
          {["staking", "brokerage", "referral_rewards"].map((type) => {
            const items = Array.isArray(logs[type]) ? logs[type] : [];
            const label = type === "referral_rewards" ? "Referral rewards" : type.charAt(0).toUpperCase() + type.slice(1);
            return (
              <div key={type} className="urn-member-logs">
                <h4>
                  {label} <span>({items.length})</span>
                </h4>
                {items.length === 0 ? (
                  <p className="urn-muted">No records.</p>
                ) : (
                  <div className="urn-table-wrap">
                    <table className="urn-table urn-table--compact">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Currency</th>
                          <th>Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((r) => (
                          <tr key={r._id}>
                            <td>{formatDate(r.created_at ?? r.credited_at)}</td>
                            <td>{formatAmount(r.amount)}</td>
                            <td>{r.currency || "—"}</td>
                            <td>{commissionDetail(r)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const UserReferralNetworkPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const traderFromState = location.state?.trader;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [network, setNetwork] = useState(null);
  const [memberLevelFilter, setMemberLevelFilter] = useState("all");
  const [commissionTab, setCommissionTab] = useState("all");
  const [expandedMember, setExpandedMember] = useState(null);
  const [mainView, setMainView] = useState("overview");

  const fetchNetwork = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const res = await AdminService.getReferralUserNetwork(userId);
      if (res?.success && res.data) {
        setNetwork(res.data);
      } else {
        setNetwork(null);
        setError(res?.message || "Failed to load referral network.");
      }
    } catch (err) {
      setNetwork(null);
      setError(err?.response?.data?.message || err?.message || "Failed to load referral network.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchNetwork();
  }, [fetchNetwork]);

  const user = network?.user;
  const summary = network?.summary;
  const levels = network?.levels || [];
  const history = network?.commission_history;
  const totalEarned = summary?.total_earned_from_network;

  const filteredMembers = useMemo(() => {
    if (memberLevelFilter === "all") {
      return levels.flatMap((l) => (l.members || []).map((m) => ({ ...m, level: m.level ?? l.level })));
    }
    const lvl = levels.find((l) => String(l.level) === memberLevelFilter);
    return (lvl?.members || []).map((m) => ({ ...m, level: m.level ?? lvl.level }));
  }, [levels, memberLevelFilter]);

  const commissionRows = useMemo(() => {
    if (!history) return [];
    if (commissionTab === "all") return history.all || [];
    return history[commissionTab] || [];
  }, [history, commissionTab]);

  const levelSummaryCards = useMemo(() => {
    const byLevel = summary?.by_level || {};
    return [1, 2, 3, 4, 5].map((n) => ({
      level: n,
      ...(byLevel[String(n)] || { members: 0, earnings: null }),
    }));
  }, [summary]);

  const backLabel = traderFromState?.name || traderFromState?.emailId || "Traders list";

  return (
    <main className="dashboard-main dashboard-subpage urn-page">
      <section className="dashboard-hero urn-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/tradelist">Traders</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Commission network</span>
          </p>
          <h1 className="dashboard-hero__title">Referral & Commission Network</h1>
          <p className="dashboard-hero__subtitle">
            {loading ? "Loading…" : `${userName(user)} · ${userEmail(user)}`}
          </p>
        </div>
        <div className="urn-hero__icon" aria-hidden>
          <i className="fas fa-project-diagram" />
        </div>
      </section>

      <div className="dashboard-body urn-body">
        <button
          type="button"
          className="urn-back-btn"
          onClick={() => navigate("/dashboard/tradelist")}
        >
          <i className="fas fa-arrow-left" /> Back to {backLabel}
        </button>

        {loading ? (
          <div className="urn-empty">
            <i className="fas fa-spinner fa-spin" />
            <p>Loading referral network…</p>
          </div>
        ) : error ? (
          <div className="urn-empty urn-empty--error">
            <i className="fas fa-exclamation-circle" />
            <p>{error}</p>
          </div>
        ) : (
          <>
            <section className="urn-card urn-user-card">
              <h2 className="urn-card__title">
                <i className="fas fa-user-circle" /> Root user
              </h2>
              <div className="urn-user-grid">
                <div><span>Name</span><strong>{userName(user)}</strong></div>
                <div><span>Email</span><strong>{userEmail(user)}</strong></div>
                <div><span>Mobile</span><strong>{user?.mobileNumber ?? user?.mobile_masked ?? "—"}</strong></div>
                <div><span>UUID</span><strong>{user?.uuid || "—"}</strong></div>
                <div><span>Referral code</span><strong><code>{user?.referral_code || "—"}</code></strong></div>
                <div><span>Total referrals</span><strong>{user?.total_refer ?? 0}</strong></div>
                <div><span>KYC</span><strong>{user?.kycVerified === 1 ? "Verified" : "Pending"}</strong></div>
                <div><span>Payout status</span><strong>{user?.payout_status || "—"}</strong></div>
                <div><span>Brokerage fee</span><strong>{user?.brokerageFee === true ? "On" : user?.brokerageFee === false ? "Off" : "—"}</strong></div>
                <div><span>Joined</span><strong>{formatDate(user?.createdAt)}</strong></div>
              </div>
              {user?.sponsered_by && (
                <div className="urn-sponsor">
                  <h3>Sponsored by</h3>
                  <div className="urn-sponsor__box">
                    <strong>{userName(user.sponsered_by)}</strong>
                    <span>{userEmail(user.sponsered_by)}</span>
                    <span>Code: {user.sponsered_by.referral_code || "—"} · UUID: {user.sponsered_by.uuid || "—"}</span>
                  </div>
                </div>
              )}
            </section>

            <section className="urn-stats">
              <div className="urn-stat urn-stat--staking">
                <span className="urn-stat__label">Staking commission</span>
                <span className="urn-stat__value">{formatAmount(totalEarned?.staking_commission?.total)}</span>
                <span className="urn-stat__sub">{totalEarned?.staking_commission?.count ?? 0} records</span>
              </div>
              <div className="urn-stat urn-stat--brokerage">
                <span className="urn-stat__label">Brokerage</span>
                <span className="urn-stat__value">{formatAmount(totalEarned?.brokerage?.total)}</span>
                <span className="urn-stat__sub">{totalEarned?.brokerage?.count ?? 0} records</span>
              </div>
              <div className="urn-stat urn-stat--referral">
                <span className="urn-stat__label">Referral rewards</span>
                <span className="urn-stat__value">{formatAmount(totalEarned?.referral_rewards?.total)}</span>
                <span className="urn-stat__sub">{totalEarned?.referral_rewards?.count ?? 0} records</span>
              </div>
              <div className="urn-stat urn-stat--combined">
                <span className="urn-stat__label">Combined total</span>
                <span className="urn-stat__value">{formatAmount(totalEarned?.combined_total)}</span>
                <span className="urn-stat__sub">{summary?.downline_count ?? 0} downline members</span>
              </div>
            </section>

            <section className="urn-card">
              <h2 className="urn-card__title">
                <i className="fas fa-layer-group" /> Earnings by level
              </h2>
              <div className="urn-level-grid">
                {levelSummaryCards.map((l) => (
                  <div key={l.level} className="urn-level-card">
                    <div className="urn-level-card__head">
                      <span>Level {l.level}</span>
                      <strong>{l.members ?? 0} members</strong>
                    </div>
                    <EarningsMini earnings={l.earnings} />
                  </div>
                ))}
              </div>
            </section>

            <div className="urn-main-tabs">
              {[
                { key: "overview", label: "Network flow", icon: "fa-project-diagram" },
                { key: "members", label: "Members", icon: "fa-users" },
                { key: "transactions", label: "All transactions", icon: "fa-list" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`urn-main-tab ${mainView === tab.key ? "urn-main-tab--active" : ""}`}
                  onClick={() => setMainView(tab.key)}
                >
                  <i className={`fas ${tab.icon}`} /> {tab.label}
                </button>
              ))}
            </div>

            {mainView === "overview" && (
              <section className="urn-card">
                <h2 className="urn-card__title">
                  <i className="fas fa-sitemap" /> Network flow diagram
                </h2>
                <p className="urn-card__hint">
                  Root user at top; each band is a referral level (L1 direct, L2 their referrals, up to L5).
                </p>
                <NetworkFlowDiagram rootUser={user} levels={levels} summary={summary} />
              </section>
            )}

            {mainView === "members" && (
              <section className="urn-card">
                <div className="urn-subtabs">
                  {LEVEL_TABS.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      className={`urn-subtab ${memberLevelFilter === tab ? "urn-subtab--active" : ""}`}
                      onClick={() => setMemberLevelFilter(tab)}
                    >
                      {tab === "all" ? "All levels" : `Level ${tab}`}
                    </button>
                  ))}
                </div>
                {filteredMembers.length === 0 ? (
                  <div className="urn-empty urn-empty--inline">
                    <i className="fas fa-users" />
                    <p>No members at this level.</p>
                  </div>
                ) : (
                  <div className="urn-members-list">
                    {filteredMembers.map((m) => (
                      <MemberRow
                        key={m._id}
                        member={m}
                        expanded={expandedMember === m._id}
                        onToggle={() =>
                          setExpandedMember((prev) => (prev === m._id ? null : m._id))
                        }
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {mainView === "transactions" && (
              <section className="urn-card">
                <div className="urn-subtabs">
                  {COMMISSION_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`urn-subtab ${commissionTab === tab.key ? "urn-subtab--active" : ""}`}
                      onClick={() => setCommissionTab(tab.key)}
                    >
                      {tab.label}
                      {tab.key === "all" && history?.total_records != null && (
                        <span className="urn-subtab__count">{history.total_records}</span>
                      )}
                    </button>
                  ))}
                </div>
                {commissionRows.length === 0 ? (
                  <div className="urn-empty urn-empty--inline">
                    <i className="fas fa-file-invoice-dollar" />
                    <p>No commission records.</p>
                  </div>
                ) : (
                  <div className="urn-table-wrap">
                    <table className="urn-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>From user</th>
                          <th>Type</th>
                          <th>Level</th>
                          <th>Amount</th>
                          <th>Currency</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commissionRows.map((row) => (
                          <tr key={row._id}>
                            <td>{formatDate(row.created_at ?? row.credited_at)}</td>
                            <td>
                              <div className="urn-table-user">
                                <strong>{userName(row.from_user)}</strong>
                                <span>{userEmail(row.from_user)}</span>
                              </div>
                            </td>
                            <td>
                              <span className={sourceBadgeClass(row)}>{sourceLabel(row)}</span>
                            </td>
                            <td>L{row.level ?? "—"}</td>
                            <td>{formatAmount(row.amount)}</td>
                            <td>{row.currency || "—"}</td>
                            <td>{commissionDetail(row)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default UserReferralNetworkPage;
